/**
 * My Work — Unified Inbox
 * Consolidates: My Recovery Work, My Plan Reviews, My Exercise Tests, My Application Profiles
 * For process owners/contributors
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Inbox, Package, ClipboardCheck, Activity, Cpu,
  RefreshCw, Clock, CheckCircle2, AlertTriangle, Eye,
  ArrowRight, Calendar, FileCheck, ListTodo,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  in_review: "bg-blue-100 text-blue-700 border-blue-200",
  submitted: "bg-sky-100 text-sky-700 border-sky-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  overdue: "bg-red-100 text-red-700 border-red-200",
  scheduled: "bg-violet-100 text-violet-700 border-violet-200",
  not_started: "bg-slate-100 text-slate-700 border-slate-200",
};

const TYPE_CONFIG = {
  package: { label: "Recovery Package", icon: Package, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-l-emerald-500" },
  task: { label: "Recovery Task", icon: ListTodo, color: "text-blue-600", bg: "bg-blue-50", border: "border-l-blue-500" },
  review: { label: "Plan Review", icon: ClipboardCheck, color: "text-sky-600", bg: "bg-sky-50", border: "border-l-sky-500" },
  exercise: { label: "Exercise Test", icon: Activity, color: "text-amber-600", bg: "bg-amber-50", border: "border-l-amber-500" },
  profile: { label: "App Profile", icon: Cpu, color: "text-purple-600", bg: "bg-purple-50", border: "border-l-purple-500" },
};

export default function MyWorkPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "all";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [workItems, setWorkItems] = useState([]);

  const fetchAllWork = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const items = [];

      // Fetch recovery packages
      try {
        const pkgRes = await api.get("/api/recovery-packages/my-packages");
        if (pkgRes.data?.data) {
          pkgRes.data.data.forEach((p) => {
            items.push({
              id: p.id || p.package_id,
              type: "package",
              title: p.package_name || p.name || "Recovery Package",
              process: p.process_name || "",
              status: p.status || "pending",
              date: p.updated_at || p.created_at || "",
              dueDate: p.due_date || "",
              action: () => navigate(`/recovery-wizard/package/${p.id || p.package_id}`),
            });
          });
        }
      } catch {}

      // Fetch recovery tasks
      try {
        const taskRes = await api.get("/api/recovery-tasks/my-tasks");
        if (taskRes.data?.data) {
          taskRes.data.data.forEach((t) => {
            items.push({
              id: t.id || t.task_id,
              type: "task",
              title: t.task_name || t.name || "Recovery Task",
              process: t.plan_name || "",
              status: t.status || "pending",
              date: t.updated_at || t.created_at || "",
              dueDate: t.due_date || "",
              action: () => navigate(`/my-recovery-tasks`),
            });
          });
        }
      } catch {}

      // Fetch plan reviews
      try {
        const reviewRes = await api.get("/api/recovery-plans/my-reviews");
        if (reviewRes.data?.data) {
          reviewRes.data.data.forEach((r) => {
            items.push({
              id: r.plan_id || r.id,
              type: "review",
              title: r.plan_name || r.name || "Plan Review",
              process: r.department || "",
              status: r.review_status || r.status || "pending",
              date: r.assigned_at || r.created_at || "",
              dueDate: r.review_deadline || "",
              action: null,
            });
          });
        }
      } catch {}

      // Fetch exercise tests
      try {
        const exRes = await api.get("/api/exercises/my-tests");
        if (exRes.data?.data) {
          exRes.data.data.forEach((e) => {
            items.push({
              id: e.id || e.exercise_id,
              type: "exercise",
              title: e.name || e.exercise_name || "Exercise Test",
              process: e.type || e.exercise_type || "",
              status: e.participant_status || e.status || "scheduled",
              date: e.scheduled_date || e.created_at || "",
              dueDate: e.scheduled_date || "",
              action: null,
            });
          });
        }
      } catch {}

      // Fetch application profiles
      try {
        const profRes = await api.get("/api/application-profiles/my-profiles");
        if (profRes.data?.data) {
          profRes.data.data.forEach((p) => {
            items.push({
              id: p.id || p.profile_id,
              type: "profile",
              title: p.application_name || p.name || "Application Profile",
              process: p.team_name || "",
              status: p.status || "draft",
              date: p.updated_at || p.created_at || "",
              dueDate: "",
              action: p.id ? () => navigate(`/application-profile/${p.id}`) : null,
            });
          });
        }
      } catch {}

      // Sort by priority: overdue > pending > in_progress > others
      const priorityOrder = { overdue: 0, rejected: 1, pending: 2, in_progress: 3, in_review: 3, submitted: 4, draft: 5, scheduled: 6, approved: 7, completed: 8, not_started: 9 };
      items.sort((a, b) => (priorityOrder[a.status] ?? 5) - (priorityOrder[b.status] ?? 5));

      setWorkItems(items);
    } catch {
      toast.error("Failed to load work items");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, navigate]);

  useEffect(() => { fetchAllWork(); }, [fetchAllWork]);

  const counts = {
    all: workItems.length,
    package: workItems.filter((i) => i.type === "package").length,
    task: workItems.filter((i) => i.type === "task").length,
    review: workItems.filter((i) => i.type === "review").length,
    exercise: workItems.filter((i) => i.type === "exercise").length,
    profile: workItems.filter((i) => i.type === "profile").length,
  };

  const filtered = activeTab === "all" ? workItems : workItems.filter((i) => {
    if (activeTab === "tasks") return i.type === "package" || i.type === "task";
    if (activeTab === "reviews") return i.type === "review";
    if (activeTab === "exercises") return i.type === "exercise";
    if (activeTab === "profiles") return i.type === "profile";
    return true;
  });

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; }
  };

  return (
    <div className="space-y-6" data-testid="my-work-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-600 to-teal-500 flex items-center justify-center shadow-sm">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            My Work
          </h1>
          <p className="text-sm text-slate-500 mt-1 ml-[52px]">
            All your assigned tasks, reviews, and action items in one place
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchAllWork(true)} disabled={refreshing} data-testid="my-work-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-3">
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <Card key={key} className={`cursor-pointer transition-all hover:shadow-md ${activeTab === key || (activeTab === "tasks" && (key === "package" || key === "task")) ? "ring-2 ring-sky-200" : ""}`}
              onClick={() => setActiveTab(key === "package" || key === "task" ? "tasks" : key === "review" ? "reviews" : key === "exercise" ? "exercises" : "profiles")}
              data-testid={`stat-${key}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${cfg.color}`} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{counts[key]}</p>
                  <p className="text-[10px] text-slate-500">{cfg.label}s</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="all" className="text-xs" data-testid="tab-all">All <Badge className="ml-1 bg-slate-200 text-slate-600 text-[9px]">{counts.all}</Badge></TabsTrigger>
          <TabsTrigger value="tasks" className="text-xs" data-testid="tab-tasks">Recovery <Badge className="ml-1 bg-slate-200 text-slate-600 text-[9px]">{counts.package + counts.task}</Badge></TabsTrigger>
          <TabsTrigger value="reviews" className="text-xs" data-testid="tab-reviews">Reviews <Badge className="ml-1 bg-slate-200 text-slate-600 text-[9px]">{counts.review}</Badge></TabsTrigger>
          <TabsTrigger value="exercises" className="text-xs" data-testid="tab-exercises">Exercises <Badge className="ml-1 bg-slate-200 text-slate-600 text-[9px]">{counts.exercise}</Badge></TabsTrigger>
          <TabsTrigger value="profiles" className="text-xs" data-testid="tab-profiles">App Profiles <Badge className="ml-1 bg-slate-200 text-slate-600 text-[9px]">{counts.profile}</Badge></TabsTrigger>
        </TabsList>

        {["all", "tasks", "reviews", "exercises", "profiles"].map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-16 gap-3">
                <div className="w-5 h-5 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-slate-500">Loading work items...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No work items found</p>
                <p className="text-xs text-slate-400 mt-1">Tasks will appear here when assigned to you</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((item) => {
                  const cfg = TYPE_CONFIG[item.type];
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={`bg-white border rounded-lg p-4 border-l-4 ${cfg.border} hover:shadow-sm transition-all flex items-center gap-4 cursor-pointer`}
                      onClick={item.action || undefined}
                      data-testid={`work-item-${item.type}-${item.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800 truncate">{item.title}</span>
                          <Badge className={`text-[9px] border ${STATUS_COLORS[item.status] || STATUS_COLORS.draft}`}>
                            {item.status.replace(/_/g, " ")}
                          </Badge>
                        </div>
                        {item.process && <p className="text-xs text-slate-400 mt-0.5">{item.process}</p>}
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0 text-xs text-slate-400">
                        <Badge variant="outline" className={`text-[9px] ${cfg.bg} ${cfg.color} border-0`}>{cfg.label}</Badge>
                        {item.dueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {formatDate(item.dueDate)}
                          </span>
                        )}
                        {item.date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatDate(item.date)}
                          </span>
                        )}
                        {item.action && <ArrowRight className="w-4 h-4 text-slate-300" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
